import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import BluetoothSpeakerCalibrationTable from "./bluetooth-speaker-calibration-table";
import {
  getBluetoothSpeakerCalibrations,
  removeBluetoothSpeakerCalibrationById,
} from "../utils/NetworkHandler";

const INITIAL_FILTERS = {
  date: "",
  speakerModel: "",
  model: "",
  brand: "",
  manufacturer: "",
  deviceId: "",
  calibrator: "",
};

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
}

export default function BluetoothSpeakerCalibrationsPage({ isDetailedData }) {
  const [rowData, setRowData] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [numberOfAllElements, setNumberOfAllElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(
    async ({
      filtersToUse = filters,
      pageToLoad = page,
      rowsPerPageToUse = rowsPerPage,
    } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await getBluetoothSpeakerCalibrations({
          ...filtersToUse,
          pageSize: rowsPerPageToUse,
          page: pageToLoad,
          sortBy: "-createdAt",
        });

        setRowData(Array.isArray(data?.content) ? data.content : []);
        setNumberOfAllElements(data?.totalElements ?? 0);
        setPage(pageToLoad);
      } catch (requestError) {
        console.error(
          "Nie udało się pobrać kalibracji głośników BT",
          requestError,
        );
        setRowData([]);
        setNumberOfAllElements(0);
        setError(
          getErrorMessage(
            requestError,
            "Nie udało się pobrać kalibracji głośników BT.",
          ),
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, page, rowsPerPage],
  );

  useEffect(() => {
    loadData({
      filtersToUse: INITIAL_FILTERS,
      pageToLoad: 0,
      rowsPerPageToUse: 20,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inputHandler = async (event, headerKey, isClear = false) => {
    const value = isClear ? "" : event?.target?.value ?? "";
    const nextFilters = {
      ...filters,
      [headerKey]: value,
    };

    setFilters(nextFilters);
    await loadData({
      filtersToUse: nextFilters,
      pageToLoad: 0,
    });
  };

  const handleChangePage = async (event, newPage) => {
    await loadData({ pageToLoad: newPage });
  };

  const handleChangeRowsPerPage = async (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    await loadData({
      pageToLoad: 0,
      rowsPerPageToUse: nextRowsPerPage,
    });
  };

  const refreshCurrentPage = useCallback(async () => {
    await loadData({ pageToLoad: page });
  }, [loadData, page]);

  const handleConfirmDelete = async () => {
    if (!itemToDelete || deleting) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await removeBluetoothSpeakerCalibrationById(itemToDelete);
      const pageToLoad = rowData.length === 1 && page > 0 ? page - 1 : page;
      setItemToDelete(null);
      await loadData({ pageToLoad });
    } catch (requestError) {
      console.error(
        "Nie udało się usunąć kalibracji głośnika BT",
        requestError,
      );
      setError(
        getErrorMessage(
          requestError,
          "Nie udało się usunąć kalibracji głośnika BT.",
        ),
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {error ? (
        <Typography color="error" style={{ marginBottom: 12 }}>
          {error}
        </Typography>
      ) : null}

      <BluetoothSpeakerCalibrationTable
        rowData={rowData}
        page={page}
        rowsPerPage={rowsPerPage}
        count={numberOfAllElements}
        inputHandler={inputHandler}
        filters={filters}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        isDetailedData={isDetailedData}
        loading={loading}
        updateData={refreshCurrentPage}
        removeHandler={setItemToDelete}
      />

      <Dialog
        open={Boolean(itemToDelete)}
        onClose={() => !deleting && setItemToDelete(null)}
      >
        <DialogTitle>Czy na pewno chcesz usunąć tę kalibrację?</DialogTitle>
        <DialogContent>
          <DialogContentText>Tej operacji nie można cofnąć.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemToDelete(null)} disabled={deleting}>
            Anuluj
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            disabled={deleting}
            autoFocus
          >
            {deleting ? "Usuwanie..." : "Usuń"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
